import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { Select } from '@ngxs/store';
import { UserModel } from '@app/modules/user/model/user';
import { UserState } from '@app/modules/user/store/user.state';

interface AdminNavItem {
  label: string;
  icon: string;
  link: string;
  /** Extra prefixes that should also light this item up (e.g. redirects). */
  activePrefixes?: string[];
  exact?: boolean;
}

/**
 * Persistent left navigation for ADMIN / PLATFORM_ADMIN users across /office.
 * Patients, doctors and proxies never see it (template renders nothing for
 * them), so their existing navigation is untouched. Collapses to a horizontal
 * strip on small screens — mirrors patient-sidebar.
 */
@Component({
  selector: 'app-admin-sidebar',
  templateUrl: './admin-sidebar.component.html',
  styleUrls: ['./admin-sidebar.component.sass'],
})
export class AdminSidebarComponent {
  @Select(UserState.model) public user$: Observable<UserModel>;

  public readonly items: AdminNavItem[] = [
    { label: 'Account Approvals', icon: 'pi-user-plus', link: '/office/admin/accounts' },
    { label: 'Organizations', icon: 'pi-sitemap', link: '/office/admin/organizations' },
    { label: 'Devices', icon: 'pi-mobile', link: '/office/admin/devices' },
    { label: 'Audit Log', icon: 'pi-history', link: '/office/admin/audit' },
    { label: 'Reports', icon: 'pi-download', link: '/office/admin/reports' },
    // Security lives in the account dropdown ("Security & two-factor") —
    // it's an account concern, not an admin destination.
  ];

  constructor(private readonly router: Router) {}

  /** Presentation-only gate: the rail renders for admin roles only. */
  public isAdmin(user: UserModel): boolean {
    const role = user && String(user.role);
    return role === 'admin' || role === 'platform_admin';
  }

  public isActive(item: AdminNavItem): boolean {
    const url = this.router.url.split('?')[0];
    if (item.exact) {
      return url === item.link;
    }
    return (item.activePrefixes || [item.link]).some((prefix) => url === prefix || url.startsWith(`${prefix}/`));
  }
}
